import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { Client } from './Client'
import { Field } from './Field'
import { Image } from './Image'
import { Order } from './Order'
import { PSRecord } from './PSRecord'
import { FKType } from './SQLBuilder'
import { Subaccount } from './Subaccount'
import { User } from './User'

type ChangeConstructorArgs = ConstructorArgs<() => Change>

export class Change extends BaseRecord {
    readonly recordType = 'change'

    image?: Image
    cancelledDate?: moment.Moment
    readonly lastRequestDate?: moment.Moment

    constructor(data?: Partial<ChangeConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastRequestDate = this.propertyFromTimestampArg(data.last_request_date)
    }

    clone(overrides?: Partial<ChangeConstructorArgs>): Change {
        return new Change({ ...this.serializeForClone(), ...overrides })
    }

    // todo: implement Customizable
    // get instance(): Customizable | undefined {
    //     if (this.client)
    //         return this.client
    //     return this.subaccount
    // }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.client) this.client = BaseRecord.fromSerializer(data.client, Client)
        if (data.record) this.record = BaseRecord.fromSerializer(data.record, PSRecord)
        if (data.booking) this.booking = BaseRecord.fromSerializer(data.booking, Booking)
        if (data.subaccount)
            this.subaccount = BaseRecord.fromSerializer(data.subaccount, Subaccount)
        if (data.order) this.order = BaseRecord.fromSerializer(data.order, Order)
        if (data.field) this.field = BaseRecord.fromSerializer(data.field, Field)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
    }

    static dataType(): DataType {
        return 'changes'
    }

    static fk: FKType = {
        users: ['owner', 'person', 'creator'],
        clients: ['client'],
        records: ['record'],
        bookings: ['booking'],
        subaccounts: ['subaccount'],
        orders: ['order'],
        fields: ['field'],
        images: ['image'],
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get field(): Field | undefined {
        return this.getForeignRecord('field', Field)
    }
    set field(field: Field | undefined) {
        this.setForeignRecord('field', field)
    }
    get client(): Client | undefined {
        return this.getForeignRecord('client', Client)
    }
    set client(client: Client | undefined) {
        this.setForeignRecord('client', client)
    }
    get order(): Order | undefined {
        return this.getForeignRecord('order', Order)
    }
    set order(order: Order | undefined) {
        this.setForeignRecord('order', order)
    }
    get record(): PSRecord | undefined {
        return this.getForeignRecord('record', PSRecord)
    }
    set record(record: PSRecord | undefined) {
        this.setForeignRecord('record', record)
    }
    get booking(): Booking | undefined {
        return this.getForeignRecord('booking', Booking)
    }
    set booking(booking: Booking | undefined) {
        this.setForeignRecord('booking', booking)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get subaccount(): Subaccount | undefined {
        return this.getForeignRecord('subaccount', Subaccount)
    }
    set subaccount(subaccount: Subaccount | undefined) {
        this.setForeignRecord('subaccount', subaccount)
    }
}
